// @need-translate
import { ReactElement, useEffect } from 'react';
import { TrackPageViewContainer } from '../../../../../common/containers/TrackPageViewContainer';
import { getEnv } from '../../../../../common/env/env';
import { getDehydratedState } from '../../../../../common/getDehydrateState';
import { redirectInInitialProps } from '../../../../../common/redirectInInitialProps';
import { requestContext } from '../../../../../common/requestContext';
import { COMMAND_KEY, useCommandWithLocalStorage } from '../../../../../common/shared/hooks/useCommandWithLocalStorage';
import { IS_CLIENT_SIDE } from '../../../../../common/shared/utils/constants';
import useTypedDispatch from '../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../common/useTypedSelector';
import { Layout } from '../../../../../design-system/layout/Layout';
import { PcHeaderType } from '../../../../../design-system/layout/PcHeaderType.enum';
import { viewerHistory } from '../../../../../domain/content/[seriesId]/viewer/[productId]/constant';
import { PULL_TO_REFRESH_BLOCK_STYLE } from '../../../../../domain/content/[seriesId]/viewer/[productId]/constant/videoViewerConstant';
import { SLIDE_TYPE_TO_PAGE_LOG_VIEWER_TYPE_MAP, VIEWER_GUIDE_TO_PAGE_LOG_VIEWING_MODE_MAP } from '../../../../../domain/content/[seriesId]/viewer/[productId]/constant/viewerConstant';
import { ViewerContainer } from '../../../../../domain/content/[seriesId]/viewer/[productId]/container/ViewerContainer';
import { ViewerEntryCheckerHeadless } from '../../../../../domain/content/[seriesId]/viewer/[productId]/headless/ViewerEntryCheckerHeadless';
import { ViewerInfoFetcherHeadless } from '../../../../../domain/content/[seriesId]/viewer/[productId]/headless/ViewerInfoFetcherHeadless';
import { ViewerOpenPageFetcherHeadless } from '../../../../../domain/content/[seriesId]/viewer/[productId]/headless/ViewerOpenPageFetcherHeadless';
import { ViewerSettingLocalStorageFetcherHeadless } from '../../../../../domain/content/[seriesId]/viewer/[productId]/headless/ViewerSettingLocalStorageFetcherHeadless';
import { clearCurrentViewerIds, setCurrentViewerIds } from '../../../../../domain/content/[seriesId]/viewer/[productId]/state/viewerInfoSlice';
import { ApiErrorBoundary } from '../../../../../errors/ApiErrorBoundary.class';
import { PagewebErrorType } from '../../../../../errors/PagewebErrorType.enum';
import { contentFactory } from '../../../../../factory/contentFactory';
import { storeFactory } from '../../../../../factory/storeFactory';
import { ExceptionMobileHeaderType } from '../../../../../header/constants';
import logger from '../../../../../log/logger';
import { MetaInfo } from '../../../../../meta/MetaInfo.interface';
import { CACHE_ONLY_CONFIG, ONLY_DATA_CONFIG } from '../../../../../reactQuery/constant';
import { createQueryClient } from '../../../../../reactQuery/createQueryClient';
import { nextReduxWrapper } from '../../../../../state/store';
import { useViewerInfoQuery } from '../../../../../wrapper/viewerWrapper';
interface ViewerProps {
  seriesId: number;
  productId: number;
  metaInfo: MetaInfo;
}

/**
 * 쇼츠 드라마 뷰어에서 pull to refresh 방지하기 위한 global style add/remove 함수입니다.
 */
function addShortsDramaGlobalStyle(styleId: string, css: string) {
  // 이미 스타일이 추가되어 있으면 중복 추가 방지
  if (!IS_CLIENT_SIDE || document.getElementById(styleId)) {
    return;
  }
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = css;
  document.head.appendChild(style);
}
function removeShortsDramaGlobalStyle(styleId: string) {
  if (!IS_CLIENT_SIDE) {
    return;
  }
  const style = document.getElementById(styleId);
  if (style) {
    document.head.removeChild(style);
  }
}
function ViewerPage({
  seriesId,
  productId
}: ViewerProps) {
  const {
    isNonstopWatching,
    pageviewLogViewingType,
    ticketUid,
    canPullToRefresh
  } = useTypedSelector(state => ({
    isNonstopWatching: !!state.viewerInfo.nonstopWatchingInSeries[seriesId]?.isNonstopWatching,
    pageviewLogViewingType: state.viewerInfo.currentViewerGuide && VIEWER_GUIDE_TO_PAGE_LOG_VIEWING_MODE_MAP[state.viewerInfo.currentViewerGuide],
    ticketUid: state.ticket.usedTicketUid[productId],
    canPullToRefresh: state.viewerInfo.canPullToRefresh
  }));
  const {
    data
  } = useViewerInfoQuery({
    seriesId,
    productId
  }, ONLY_DATA_CONFIG);
  const viewerItem = data?.item;
  const viewerSlideType = viewerItem?.slideType;
  const seriesTitle = data?.seriesItem?.title;
  const productTitle = viewerItem?.title;
  const isShortsDrama = viewerItem?.isShortsDrama;
  const category = data?.seriesItem?.category;
  const subcategory = data?.seriesItem?.subcategory;
  const dispatch = useTypedDispatch();
  const {
    runCommand
  } = useCommandWithLocalStorage();
  useEffect(() => {
    if (seriesId && productId) {
      dispatch(setCurrentViewerIds({
        currentSeriesId: seriesId,
        currentProductId: productId
      }));
    }
    return () => {
      dispatch(clearCurrentViewerIds());
    };
  }, [dispatch, productId, seriesId]);
  useEffect(() => {
    const manageViewerHistory = async () => {
      await runCommand({
        commandKey: COMMAND_KEY.TRIGGER_FROM_PREV_PRODUCT,
        action: async commandData => {
          const {
            from
          } = commandData;
          if (from) {
            viewerHistory.push(from);
          }
        }
      });
      viewerHistory.push(productId);
    };
    manageViewerHistory();
  }, [productId, runCommand]);
  useEffect(() => {
    return () => {
      viewerHistory.length = 0;
    };
  }, [dispatch]);
  useEffect(() => {
    // @TODO : textViewer 에서도 pull to refresh 방지를 하고 있으므로 공통으로 빼서 적용하는게 좋을 것 같습니다.
    // pull to refresh 방지하는 스타일을 제어합니다.
    !canPullToRefresh ? addShortsDramaGlobalStyle('shorts-drama-style', PULL_TO_REFRESH_BLOCK_STYLE) : removeShortsDramaGlobalStyle('shorts-drama-style');
  }, [canPullToRefresh]);
  useEffect(() => {
    return () => {
      removeShortsDramaGlobalStyle('shorts-drama-style');
    };
  }, []);

  /**
   * nextjs의 prerender 과정에서 에러가 발생해서 예외처리를 추가했습니다.
   */
  if (!requestContext.requestInfo || !seriesId || !productId) {
    return null;
  }
  const pageviewLogViewerName = viewerSlideType ? SLIDE_TYPE_TO_PAGE_LOG_VIEWER_TYPE_MAP[viewerSlideType] : undefined;
  return <ApiErrorBoundary>
      <ViewerEntryCheckerHeadless productId={productId} seriesId={seriesId}>
        <ViewerInfoFetcherHeadless seriesId={seriesId} productId={productId}>
          <ViewerOpenPageFetcherHeadless seriesId={seriesId} productId={productId} ticketUid={ticketUid} />
          <ViewerSettingLocalStorageFetcherHeadless seriesId={seriesId} productId={productId}>
            {pageviewLogViewerName && <TrackPageViewContainer
          // 웹뷰일경우의 뷰어 PV는 앱에서 기록합니다.
          sendTrackEvent={!requestContext.requestInfo.display.isWebView} trackObj={{
            shouldSendUsagePage: true,
            section: '뷰어',
            page: pageviewLogViewerName,
            actionKind: 'ViewContent',
            actionType: 'Pageview',
            actionName: `${pageviewLogViewerName}_화면`,
            page_meta: {
              id: String(productId),
              type: 'productId',
              // seriesId는 pageView에는 불필요한 값이지만 뷰어 내 다른 이벤트로깅에 사용되므로 넣어줍니다.
              series: seriesTitle,
              series_id: String(seriesId),
              // name은 pageView엔 안쓰이지만 뷰어 진입 이벤트에 쓰입니다.
              name: productTitle,
              category,
              subcategory
            },
            customProps: {
              user_action: 'pv',
              viewer_mode: isNonstopWatching ? '정주행모드' : '일반모드',
              viewing_type: pageviewLogViewingType,
              is_offline: navigator.onLine ? 'N' : 'Y',
              is_shortdrama: isShortsDrama ? 'y' : undefined
            }
          }} />}
            <ViewerContainer seriesId={seriesId} productId={productId} />
          </ViewerSettingLocalStorageFetcherHeadless>
        </ViewerInfoFetcherHeadless>
      </ViewerEntryCheckerHeadless>
    </ApiErrorBoundary>;
}
ViewerPage.getInitialProps = nextReduxWrapper.getInitialPageProps(() => async context => {
  const seriesId = Number(context.query?.seriesId || '');
  const productId = Number(context.query?.productId || '');
  let metaInfo: MetaInfo | undefined = undefined;
  const queryClient = createQueryClient();
  try {
    const singlePreviewResult = await queryClient.fetchQuery(storeFactory.singlePreview({
      productId
    }, CACHE_ONLY_CONFIG, {
      ssrRequest: context.req
    }));
    const singleSimpleResult = await queryClient.fetchQuery(contentFactory.simpleSeriesSingleInfo({
      seriesId,
      productId
    }, CACHE_ONLY_CONFIG, {
      ssrRequest: context.req
    }));
    if (!singlePreviewResult.singlePreview) {
      throw new Error('singlePreview fetch 실패');
    }
    if (!singleSimpleResult.simpleSeriesSingleInfo) {
      throw new Error('singleSimple fetch 실패');
    }
    const {
      title,
      author,
      thumbnail,
      landThumbnail,
      seriesPreview,
      seriesLang
    } = singlePreviewResult.singlePreview;
    metaInfo = {
      title,
      ogTitle: title,
      author,
      keywords: `${title}, ${author}`,
      description: seriesPreview?.description ? seriesPreview.description.replace(/\n/g, '') : '',
      url: `${getEnv().webOrigin}/content/${seriesId}/viewer/${productId}`,
      image: landThumbnail || thumbnail,
      lang: seriesLang
    };
  } catch (e) {
    //  잘못된 input 등일 때 apolloError 가 발생. 이 경우 404 로 띄워준다
    if ('isSerializedPagewebError' in e) {
      if (e.type === PagewebErrorType.ApolloError) {
        redirectInInitialProps({
          destination: '/404',
          res: context.res
        });
        return {};
      }
    }
    logger.err('뷰어 Generation 실패', e);
  }
  return {
    seriesId,
    productId,
    metaInfo,
    ...getDehydratedState(queryClient)
  };
});
ViewerPage.getLayout = function (page: ReactElement) {
  return <Layout title={page.props.metaInfo?.title || '뷰어'} mobileLayoutConfig={{
    shouldShowHeader: true,
    headerType: ExceptionMobileHeaderType.MobileViewer
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    headerType: PcHeaderType.Viewer,
    shouldUseFixedWidth: false
  }} metaInfo={page.props.metaInfo}>
      {page}
    </Layout>;
};
export default ViewerPage;